import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { UserActionType } from '@wix/ambassador-funnel-chatbot-v1-session/types';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import { MESSAGES, ROLE } from '../../../constants';
import { isNumber } from '../../../util/guards';
import { experiments } from '../../experiments';
import { createChatMessage, isLiveChatMode } from '../../messageChannel/chatCreators';
import { sendMessageReady } from '../../messageChannel/postNewMessages';
import { renderModel } from '../../util/renderModel';
import { createBotTyping, createLoginSelector } from '../messageCreators';
import { loadArticlePreview } from '../thunks/articlePreview.thunk';
import { loadChatbotStartChat, StartChatParams } from '../thunks/chatbotStartChat.thunk';
import { changeMessage, concatItems, disableChangeFeature, omit } from './chatTransforms';
import { headerActions } from './header';
import {
  ChatScrollToPositions,
  ErrorStatus,
  IChatState,
  IUndelivered,
  IUserAction,
  ResetReason,
  RestartChatParameters,
  TItem,
} from './types';

const initialState: IChatState = {
  isLocked: false,
  isLiveChat: false,
  isMuted: false,
  isConnectingAgent: false,
  isDocumentVisible: undefined,
  conversationId: '',
  items: [],
  undelivered: {},
  unreadCount: 0,
  scrollTo: undefined,
  isFirstPriorityUser: false,
  itemsIdsOnLastPageLoad: [],
  isFromAnswerGeneration: false,
  isStartOverFromAnswerGeneration: false,
  pendingFollowUpQuestion: undefined,
};

const isCopilot = renderModel.isCopilot();

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addItem: (state, action: PayloadAction<TItem>) => {
      state.isReset = false;
      if (action.payload.role === ROLE.BOT) {
        const cancelledIndex = state.items.findIndex(
          (i) =>
            i.type === MESSAGES.AI_ARTICLE_ANSWER &&
            (i as { isCancelled?: boolean }).isCancelled &&
            i.id !== action.payload.id,
        );
        if (cancelledIndex > -1) {
          const cancelledItem = state.items[cancelledIndex];
          let replacement: TItem = action.payload;
          if (cancelledItem.type === MESSAGES.AI_ARTICLE_ANSWER) {
            if (action.payload.type === MESSAGES.AI_ARTICLE_ANSWER) {
              replacement = {
                ...action.payload,
                renderKey: cancelledItem.id,
                isStreaming: false,
                isCancelled: false,
              };
            } else if (action.payload.type === MESSAGES.BOT_MESSAGE) {
              replacement = {
                ...cancelledItem,
                id: action.payload.id,
                nodeId: action.payload.nodeId,
                rm: action.payload.rm,
                preventScroll: action.payload.preventScroll ?? false,
                answer: action.payload.html,
                renderKey: cancelledItem.id,
                isStreaming: false,
                isCancelled: false,
              };
            }
          }
          state.items = state.items.map((i, idx) => (idx === cancelledIndex ? replacement : i));
          return;
        }
      }
      state.items = concatItems(state.items, action.payload);
      if (
        action.payload.type === MESSAGES.AI_ARTICLE_ANSWER &&
        state.streamingMessage?.messageId === action.payload.id
      ) {
        state.streamingMessage = undefined;
      }
    },
    updateStreamingChunk: (
      state,
      action: PayloadAction<{ messageId: string; accumulatedText: string; sequenceNumber: number }>,
    ) => {
      const { messageId, accumulatedText, sequenceNumber } = action.payload;
      if (
        state.streamingMessage &&
        state.streamingMessage.messageId === messageId &&
        state.streamingMessage.sequenceNumber >= sequenceNumber
      ) {
        return;
      }
      const isTabAwaitingAnswer =
        state.items.some((i) => i.type === MESSAGES.BOT_TYPING) ||
        state.items.some(
          (i) =>
            i.type === MESSAGES.AI_ARTICLE_ANSWER &&
            (i.id === messageId ||
              (i as { isStreaming?: boolean }).isStreaming ||
              (i as { isCancelled?: boolean }).isCancelled),
        );
      if (!isTabAwaitingAnswer) {
        return;
      }
      state.items = state.items.filter(
        (i) =>
          !(
            i.type === MESSAGES.AI_ARTICLE_ANSWER &&
            (i as { isCancelled?: boolean }).isCancelled &&
            i.id !== messageId
          ),
      );
      const existingIndex = state.items.findIndex(
        (i) => i.type === MESSAGES.AI_ARTICLE_ANSWER && i.id === messageId,
      );
      if (existingIndex > -1 && !(state.items[existingIndex] as { isStreaming?: boolean }).isStreaming) {
        return;
      }
      state.items = state.items.filter((i) => i.type !== MESSAGES.BOT_TYPING);
      state.streamingMessage = { messageId, accumulatedText, sequenceNumber };
      const placeholder = {
        id: messageId,
        nodeId: '',
        type: MESSAGES.AI_ARTICLE_ANSWER as const,
        answer: accumulatedText,
        articles: [],
        rm: false,
        role: ROLE.BOT,
        preventScroll: false,
        isStreaming: true,
      };
      if (existingIndex > -1) {
        state.items.splice(existingIndex, 1, placeholder);
      } else {
        state.items.push(placeholder);
      }
    },
    setAnswerAnimating: (state, action: PayloadAction<{ messageId: string; isAnimating: boolean }>) => {
      const { messageId, isAnimating } = action.payload;
      if (isAnimating) {
        state.animatingAnswerId = messageId;
      } else if (state.animatingAnswerId === messageId) {
        state.animatingAnswerId = undefined;
      }
    },
    cancelStreamingMessage: (state, action: PayloadAction<{ messageId: string }>) => {
      const { messageId } = action.payload;
      state.items = state.items.map((i) =>
        i.type === MESSAGES.AI_ARTICLE_ANSWER && i.id === messageId
          ? { ...i, isStreaming: false, isCancelled: true }
          : i,
      );
      if (state.streamingMessage?.messageId === messageId) {
        state.streamingMessage = undefined;
      }
    },
    addItemBeforeId: (state, action: PayloadAction<{ item: TItem; beforeId: string }>) => {
      const { item, beforeId } = action.payload;
      const index = state.items.findIndex((i) => i.id === beforeId);
      if (index > -1) {
        state.items.splice(index, 0, item);
      } else {
        state.items.push(item);
      }
      state.isReset = false;
    },
    changeMessage: (state) => {
      state.isLocked = true;
      state.items = changeMessage(state.items);
    },
    reset: (state, action: PayloadAction<ResetReason>) => {
      state.isLocked = false;
      state.isConnectingAgent = false;
      state.isReset = true;
      state.resetReason = action.payload;
      state.reloadStartChat = false;
      state.startChatLoading = false;
      state.pendingFollowUpQuestion = undefined;
      state.streamingMessage = undefined;
      state.animatingAnswerId = undefined;
      if (action.payload === ResetReason.SyncedStartOver) {
        state.items = [];
      } else if (state.items.length > 0 || action.payload === ResetReason.StartOver) {
        const requireLogin = !renderModel.getUserId() && !experiments.is('enableAnonymousAIChatbot', 'B');
        state.items =
          action.payload === ResetReason.StartOver && requireLogin
            ? [createLoginSelector({})]
            : [createBotTyping({ rm: true })];
      }
    },
    toggleMute: (state, action: PayloadAction<boolean>) => {
      state.isMuted = action.payload;
    },
    toggleLock: (state, action: PayloadAction<boolean>) => {
      state.isLocked = action.payload;
    },
    addUndelivered: (
      state,
      action: PayloadAction<{
        readonly [key: string]: IUserAction | UserActionType;
      }>,
    ) => {
      state.items = state.items.filter((item) => item.type !== MESSAGES.BOT_TYPING);
      state.isLocked = false;
      state.undelivered = {
        ...state.undelivered,
        ...action.payload,
      } as IUndelivered;
    },
    removeUndelivered: (state, action: PayloadAction<string>) => {
      state.undelivered = omit(state.undelivered, action.payload) as IUndelivered;
    },
    updateSession: (state, action: PayloadAction<Pick<IChatState, 'conversationId'>>) => {
      state.conversationId = action.payload.conversationId;
    },
    increaseUnreadCount: (state) => {
      const { unreadCount } = state;
      state.unreadCount = isNumber(unreadCount) ? unreadCount + 1 : 1;
    },
    updateUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = action.payload;
    },
    resetUnreadCount: (state) => {
      state.unreadCount = 0;
    },
    scrollTo: (state, action: PayloadAction<ChatScrollToPositions>) => {
      state.scrollTo = { position: action.payload };
    },
    changeRestartChatParameters: (state, action: PayloadAction<RestartChatParameters>) => {
      state.restartChatParameters = action.payload;
    },
    startChatFromNode: (state, action: PayloadAction<string>) => {
      state.startingNodeId = action.payload;
    },
    // if chatbot is open it will request start chat immediately. If no - it will request start chat when chatbot is open
    setReloadStartChat: (state, action: PayloadAction<boolean>) => {
      state.reloadStartChat = action.payload;
    },
    setStartChatParameters: (state, action: PayloadAction<StartChatParams | undefined>) => {
      // if params are not defined and new params are empty object
      if (!state.startChatParameters && isEmpty(action.payload)) {
        return;
      }
      // do not need to request a start chat of params(dealer offer params) are the same
      if (isEqual(state.startChatParameters, action.payload)) {
        return;
      }
      state.startChatParameters = action.payload;
      state.reloadStartChat = true;
    },
    setIsFirstPriorityUser: (state, action: PayloadAction<boolean>) => {
      state.isFirstPriorityUser = action.payload;
    },
    cleanUpBotTyping: (state) => {
      if (state.items.length === 1 && state.items[0].type === MESSAGES.BOT_TYPING) {
        state.items = [];
      }
      state.isReset = false;
    },
    removeBotMessageBeforeLoginSelector: (state) => {
      let loginSelectorIndex = -1;
      for (let i = state.items.length - 1; i >= 0; i--) {
        if (state.items[i]?.type === MESSAGES.LOGIN_SELECTOR) {
          loginSelectorIndex = i;
          break;
        }
      }

      if (loginSelectorIndex <= 0) {
        return;
      }

      for (let i = loginSelectorIndex - 1; i >= 0; i--) {
        if (state.items[i]?.type === MESSAGES.BOT_MESSAGE) {
          state.items.splice(i, 1);
          return;
        }
      }
    },
    startConnectingAgent: (state) => {
      state.isConnectingAgent = true;
    },
    setIsDocumentVisible: (state, action: PayloadAction<boolean>) => {
      state.isDocumentVisible = action.payload;
    },
    setIsFromAnswerGeneration: (state, action: PayloadAction<boolean>) => {
      state.isFromAnswerGeneration = action.payload;
    },
    setIsStartOverFromAnswerGeneration: (state, action: PayloadAction<boolean>) => {
      state.isStartOverFromAnswerGeneration = action.payload;
    },
    setPendingFollowUpQuestion: (state, action: PayloadAction<string | undefined>) => {
      state.pendingFollowUpQuestion = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(headerActions.changeChatMode, (state, action) => {
      state.isLocked = false;
      state.isLiveChat = action.payload;
      state.isConnectingAgent = false;
      // When we toggle to live chat then we need to disable all change buttons in the user messages
      state.items = action.payload ? disableChangeFeature([...state.items]) : state.items;
    });
    builder.addCase(loadChatbotStartChat.pending, (state) => {
      state.startChatLoading = true;
      state.startChatError = undefined;
    });

    builder.addCase(loadChatbotStartChat.fulfilled, (state, action) => {
      const { startChatData, unreadMessagesCount, messages } = action.payload;
      const isLiveChat = isLiveChatMode(startChatData?.mode);
      const items: TItem[] = messages?.reduce((acc: TItem[], message: unknown) => {
        const item = createChatMessage(message);
        return item ? concatItems(acc, item) : acc;
      }, []);
      if (isCopilot) {
        sendMessageReady();
      }

      state.isLiveChat = isLiveChat;
      state.isFirstPriorityUser = startChatData?.priorityOneUser;
      state.conversationId = startChatData?.conversationId;
      state.items = items;
      state.itemsIdsOnLastPageLoad = items.map(({ id }) => id);
      state.unreadCount = unreadMessagesCount || 0;
      state.startChatLoading = false;
      state.startChatError = undefined;
      state.reloadStartChat = false;
      state.isReset = false;
      state.articlePreviews = {}; // remove fetched articles for previous conversation
      state.streamingMessage = undefined;

      if (state.startChatParameters?.shouldStartNewChat === 'true') {
        const { shouldStartNewChat: _shouldStartNewChat, ...rest } = state.startChatParameters;
        state.startChatParameters = isEmpty(rest) ? undefined : (rest as StartChatParams);
      }
    });

    builder.addCase(loadChatbotStartChat.rejected, (state, action) => {
      state.startChatLoading = false;
      state.startChatError = action.error.message;
      if (isCopilot) {
        sendMessageReady();
      }
    });

    builder.addCase(loadArticlePreview.pending, (state, action) => {
      const articleId = action.meta.arg.id;
      state.articlePreviews = {
        ...state.articlePreviews,
        [articleId]: {
          article: null,
          isLoading: true,
          error: null,
        },
      };
    });

    builder.addCase(loadArticlePreview.fulfilled, (state, action) => {
      const articleId = action.meta.arg.id;
      state.articlePreviews = {
        ...state.articlePreviews,
        [articleId]: {
          article: action.payload,
          isLoading: false,
          error: null,
        },
      };
    });

    builder.addCase(loadArticlePreview.rejected, (state, action) => {
      const articleId = action.meta.arg.id;
      state.articlePreviews = {
        ...state.articlePreviews,
        [articleId]: {
          article: null,
          isLoading: false,
          error: action.payload || ErrorStatus.ServerError,
        },
      };
    });
  },
});

export const chatActions = chatSlice.actions;
